<template>
  <div>
    <!-- MACRO SEARCH EDITION -->
    <div class="container-fluid py-4" v-if="macroEdition">
      <div class="row">
        <div class="col-10 d-flex justify-content-center mx-auto">
          <not-allowed-tooltip
            :user-permissions="userPermissions"
            permission-key="canUpdateContent"
            placement="top"
          >
            <template v-slot:main-content="{ hasPermissions }">
              <el-switch
                style="display: block"
                v-model="searchByName"
                active-color="#FCBF49"
                inactive-color="#0081f9"
                :disabled="!hasPermissions"
                :active-text="
                  $t(
                    'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.search-id',
                  )
                "
                :inactive-text="
                  $t(
                    'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.search-name',
                  )
                "
              />
            </template>
          </not-allowed-tooltip>
        </div>
      </div>

      <!-- MACRO SEARCH BY NAME -->
      <not-allowed-tooltip
        v-if="!searchByName"
        :user-permissions="userPermissions"
        permission-key="canUpdateContent"
        placement="top"
      >
        <template v-slot:main-content="{ hasPermissions }">
          <div class="row mt-3">
            <el-select
              v-model="newMacroId"
              filterable
              remote
              clearable
              :disabled="!hasPermissions"
              :remote-method="searchMacros"
              :loading="remoteLoading"
              :placeholder="
                $t(
                  'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.search-macro',
                )
              "
              class="col-7 m-auto selectBorderColor"
            >
              <el-option
                v-for="option in allMacros"
                :key="option.id"
                :label="`${option.title} - ${option.id}`"
                :value="`${String(option.id)}`"
              ></el-option>
            </el-select>
            <base-button
              class="col-3 m-auto"
              type="primary"
              outline
              :disabled="!hasPermissions || newMacroId.length === 0"
              @click="updateMacro"
              >{{
                $t(
                  'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.validate',
                )
              }}
            </base-button>
          </div>
        </template>
      </not-allowed-tooltip>

      <!-- MACRO SEARCH BY ID -->
      <div class="row mt-3" v-else>
        <el-input
          class="col-7 m-auto inputSelectColor"
          placeholder="ID de la macro"
          v-model="newMacroId"
        >
          ></el-input
        >
        <base-button
          class="col-3 m-auto"
          type="primary"
          outline
          :disabled="newMacroId.length === 0"
          @click="updateMacro"
          >{{
            $t(
              'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.validate',
            )
          }}</base-button
        >
      </div>
    </div>

    <!-- MACRO DISPLAY AFTER SELECT -->
    <div class="container-fluid py-4" v-else v-loading="remoteLoading">
      <p class="col-auto macro-indication mb-0 pl-0">
        🤖
        {{
          $t(
            'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.macro-step',
          )
        }}
        <el-tooltip
          :content="
            $t(
              'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.tooltip-content',
            )
          "
          placement="top"
          class="group-tooltip"
        >
          <span class="macroId 1" @click="macroEdition = true">{{
            ` ${selectedMacro[0].title} - ${selectedMacro[0].id} `
          }}</span>
        </el-tooltip>
        {{
          $t(
            'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.macro-step-2',
          )
        }}
        <img
          class="modify-step ml-2 mb-1"
          src="~assets/edit-icon.png"
          @click="macroEdition = true"
          style="cursor: pointer"
        />
        <font-awesome-icon
          class="ml-1"
          @click="modalMacro = true"
          :icon="['fad', 'info-circle']"
          style="cursor: pointer"
        ></font-awesome-icon>
        <img
          class="col-1 px-0 modify-step step-delete ml-2 mb-1"
          src="~assets/delete-icon.png"
          @click="deleteMacro"
        />
      </p>
    </div>

    <!-- MACRO SUMMARY MODAL -->
    <macro-editor-modal
      v-if="modalMacro"
      :display="modalMacro"
      :selectedMacro="selectedMacro"
      @close="modalMacro = false"
    />
  </div>
</template>
<script>
import MacroEditorModal from 'components/Modals/MacroEditorModal';
import NotAllowedTooltip from '@/components/Commons/NotAllowedTooltip';

export default {
  name: 'macro-editor',
  components: {
    MacroEditorModal,
    NotAllowedTooltip,
  },
  props: {
    macroId: {
      type: String,
      default: '',
    },
    userPermissions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      remoteLoading: false,
      newMacroId: '',
      macroEdition: true,
      allMacros: [],
      modalMacro: false,
      searchByName: false,
      selectedMacro: [
        {
          title: '',
          id: '',
        },
      ],
    };
  },
  mounted() {
    this.newMacroId = this.macroId;
    this.macroEdition = this.macroId ? this.macroId.length === 0 : true;
    if (this.macroId !== '') {
      this.getMacroById(this.macroId);
    }
  },
  methods: {
    async searchMacros(input) {
      if (input !== '') {
        this.remoteLoading = true;
      } else {
        this.allMacros = [];
        return;
      }
      this.allMacros = await this.$services.zendesk.searchMacros(input);
      this.remoteLoading = false;
    },
    async getMacroById(id) {
      if (id === '') {
        this.macroEdition = true;
        this.selectedMacro = [];
        this.allMacros = [];
        return;
      }

      this.remoteLoading = true;
      const macro = await this.$services.zendesk.getMacroById(id);
      if (macro) {
        macro.created_at = this.applyDateParse(Date.parse(macro.created_at));
        macro.updated_at = this.applyDateParse(Date.parse(macro.updated_at));

        this.selectedMacro = [macro];
        this.allMacros = [macro];
      } else {
        this.$message({
          message: this.$t(
            'knowledge.diagnostic-builder.side-bar.triggers.add-answer.container.macro-not-found',
          ),
          type: 'error',
          center: true,
        });
        this.newMacroId = '';
        this.macroEdition = true;
        this.selectedMacro = [];
        this.allMacros = [];
      }

      this.remoteLoading = false;
    },
    applyDateParse(date) {
      let options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      };
      let formatDate = new Date(Number(date)).toLocaleDateString(
        this.$i18n.locale,
        options,
      );
      return formatDate.charAt(0).toUpperCase() + formatDate.slice(1);
    },
    updateMacro() {
      this.macroEdition = false;
      this.$emit('update-macro', {
        actions: {
          macro_id: this.newMacroId,
          type: '',
          message: '',
          assignee_group_id: '',
          assignee_user_id: '',
          email_cc: '',
          status: '',
        },
      });
    },
    deleteMacro() {
      this.macroEdition = false;
      this.newMacroId = '';
      this.$emit('update-macro');
    },
  },
  watch: {
    macroId() {
      this.getMacroById(this.macroId);
    },
  },
};
</script>
<style lang="scss" scoped>
.inputSelectColor {
  :deep() input {
    border-color: $yellow-mayday;
  }
}

.selectBorderColor {
  :deep() input {
    border-color: $blue-mayday;
  }
}

.selectBorderColor {
  :deep() input:hover {
    border-color: $blue-mayday;
  }
}

.macro-indication {
  margin-top: 10px;
}

.macro-invalid {
  :deep() input {
    border: 1px solid $red-mayday;
  }
}

.macroId:hover {
  background-color: rgba($blue-mayday, 0.1);
  color: $blue-mayday;
  cursor: pointer;
  border-radius: 2px;
}

.modify-step {
  max-width: 20px;
  cursor: pointer;
  padding-right: 2px;
}

.modify-step:hover {
  border-radius: 3px;
  background-color: $grey-2-mayday;
}
</style>
